import {
  closestCenter,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ReactNode } from "react";
import type { Item } from "../api/types";
import { moveTask } from "../sync/actions";

export const collision = closestCenter;

export function useDndSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
}

export interface Container {
  id: string;
  tasks: Item[];
  target: { project_id: string; section_id: string | null; parent_id: string | null };
}

const CONTAINER_PREFIX = "container:";
export const containerDropId = (id: string) => `${CONTAINER_PREFIX}${id}`;

/**
 * Resolve a drop into (container, index) and persist it: item_move when the container changes,
 * item_update order_key for the position among its new siblings.
 */
export function handleTaskDrop(e: DragEndEvent, containers: Container[]) {
  const activeId = String(e.active.id);
  const overId = e.over ? String(e.over.id) : null;
  if (!overId || overId === activeId) return;
  const from = containers.find((c) => c.tasks.some((t) => t.id === activeId));
  const item = from?.tasks.find((t) => t.id === activeId);
  if (!from || !item) return;
  let to: Container | undefined;
  let index: number;
  if (overId.startsWith(CONTAINER_PREFIX)) {
    to = containers.find((c) => c.id === overId.slice(CONTAINER_PREFIX.length));
    index = to ? to.tasks.filter((t) => t.id !== activeId).length : 0;
  } else {
    to = containers.find((c) => c.tasks.some((t) => t.id === overId));
    index = to ? to.tasks.findIndex((t) => t.id === overId) : 0;
  }
  if (!to) return;
  let list: Item[];
  if (to.id === from.id) {
    const oldIndex = from.tasks.findIndex((t) => t.id === activeId);
    list = arrayMove(from.tasks, oldIndex, index);
  } else {
    list = [...to.tasks];
    list.splice(index, 0, item);
  }
  const pos = list.findIndex((t) => t.id === activeId);
  if (to.id === from.id && pos === from.tasks.findIndex((t) => t.id === activeId)) return;
  void moveTask(item, to.target, list[pos - 1], list[pos + 1]);
}

export function SortableItem({
  id,
  children,
}: {
  id: string;
  children: (
    handle: {
      attributes: ReturnType<typeof useSortable>["attributes"];
      listeners: ReturnType<typeof useSortable>["listeners"];
    },
    dragging: boolean,
  ) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition }} data-sortable-id={id}>
      {children({ attributes, listeners }, isDragging)}
    </div>
  );
}

export function DroppableList({
  id,
  items,
  children,
  className,
  overClassName,
}: {
  id: string;
  items: string[];
  children: ReactNode;
  className?: string;
  overClassName?: string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: containerDropId(id) });
  return (
    <SortableContext id={id} items={items} strategy={verticalListSortingStrategy}>
      <div
        ref={setNodeRef}
        className={`${className ?? ""}${isOver && overClassName ? ` ${overClassName}` : ""}`}
        data-container={id}
      >
        {children}
      </div>
    </SortableContext>
  );
}
